import React, { useEffect, useState, useMemo } from "react";
import { Table, Button, Form, Modal, Badge, Spinner, Row, Col, InputGroup } from "react-bootstrap";
import api from "../../api";
import Can from "../../components/Can";
import { toast } from "react-toastify";
import ConfirmModal from "../../components/common/ConfirmModal";
import EmptyState from "../../components/common/EmptyState";
import { FaCalendarAlt } from "react-icons/fa";
import "./PerformancePeriodManagementPage.css";

// Phase 5, §4/§21/§32. Reporting periods — supports April-March financial
// years via period_type + explicit dates, never a hardcoded calendar year.
const STATUS_VARIANTS = { DRAFT: "secondary", OPEN: "success", CLOSED: "dark", ARCHIVED: "light" };
const initialForm = { code: "", name: "", period_type: "MONTHLY", fiscal_year_label: "", start_date: "", end_date: "" };

export default function PerformancePeriodManagementPage() {
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchPeriods = async () => {
    setLoading(true);
    try {
      const res = await api.get("/performance-periods", { params: { pageSize: 100 } });
      setPeriods(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to fetch performance periods");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPeriods(); }, []);

  const openAdd = () => { setForm(initialForm); setEditId(null); setShowModal(true); };
  const openEdit = (p) => {
    setForm({ code: p.code, name: p.name, period_type: p.period_type, fiscal_year_label: p.fiscal_year_label || "", start_date: p.start_date?.slice(0, 10) || "", end_date: p.end_date?.slice(0, 10) || "" });
    setEditId(p.id);
    setShowModal(true);
  };
  const closeModal = () => { setShowModal(false); setEditId(null); setForm(initialForm); };

  const handleSave = async () => {
    if (!form.code.trim() || !form.name.trim() || !form.start_date || !form.end_date) {
      toast.warning("Code, name, start date and end date are required");
      return;
    }
    setSaving(true);
    try {
      if (editId) {
        await api.put(`/performance-periods/${editId}`, form);
        toast.success("Period updated");
      } else {
        await api.post("/performance-periods", form);
        toast.success("Period created");
      }
      closeModal();
      fetchPeriods();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save period");
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (p, status) => {
    try {
      await api.patch(`/performance-periods/${p.id}/status`, { status });
      toast.success(`Period set to ${status}`);
      fetchPeriods();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update status");
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/performance-periods/${deleteTarget.id}`);
      toast.success("Period deleted");
      setDeleteTarget(null);
      fetchPeriods();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete period (scorecards may exist)");
    } finally {
      setDeleting(false);
    }
  };

  const hasActiveFilters = !!(search || statusFilter);
  const clearFilters = () => { setSearch(""); setStatusFilter(""); };

  const visiblePeriods = useMemo(() => {
    return periods.filter((p) => {
      if (statusFilter && p.status !== statusFilter) return false;
      if (search) {
        const q = search.trim().toLowerCase();
        if (!p.code?.toLowerCase().includes(q) && !p.name?.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [periods, search, statusFilter]);

  const stats = useMemo(() => ({
    total: periods.length,
    open: periods.filter((p) => p.status === "OPEN").length,
    draft: periods.filter((p) => p.status === "DRAFT").length,
    closed: periods.filter((p) => p.status === "CLOSED" || p.status === "ARCHIVED").length,
  }), [periods]);

  return (
    <div className="unit-page perfperiod-theme">
      <div className="unit-header">
        <div>
          <div className="unit-page-label">Performance</div>
          <h2>Performance Periods</h2>
          <p>Monthly, quarterly, half-yearly, annual or custom reporting periods — supports April-March financial years.</p>
        </div>
        <div className="unit-header-actions">
          <Button className="unit-btn unit-btn-light" onClick={fetchPeriods}>
            <i className="fa-solid fa-rotate" aria-hidden="true"></i>
            Refresh
          </Button>
          <Can moduleKey="performance_periods" actionKey="create">
            <Button className="unit-btn unit-btn-primary" onClick={openAdd}>
              <i className="fa-solid fa-plus" aria-hidden="true"></i>
              New Period
            </Button>
          </Can>
        </div>
      </div>

      <div className="unit-stats-row">
        <div className="unit-stat-card">
          <div className="perfperiod-stat-icon perfperiod-stat-icon-primary">
            <i className="fa-solid fa-calendar-days" aria-hidden="true"></i>
          </div>
          <div className="perfperiod-stat-text">
            <span>Total Periods</span>
            <strong>{stats.total}</strong>
          </div>
        </div>
        <div className="unit-stat-card">
          <div className="perfperiod-stat-icon perfperiod-stat-icon-success">
            <i className="fa-solid fa-circle-check" aria-hidden="true"></i>
          </div>
          <div className="perfperiod-stat-text">
            <span>Open</span>
            <strong>{stats.open}</strong>
          </div>
        </div>
        <div className="unit-stat-card">
          <div className="perfperiod-stat-icon perfperiod-stat-icon-accent">
            <i className="fa-solid fa-file-pen" aria-hidden="true"></i>
          </div>
          <div className="perfperiod-stat-text">
            <span>Draft</span>
            <strong>{stats.draft}</strong>
          </div>
        </div>
      </div>

      <div className="unit-card">
        <div className="perfperiod-list-header">
          <h5>
            <i className="fa-solid fa-calendar-days perfperiod-list-icon" aria-hidden="true"></i>
            Period List
          </h5>
          <p>Search and filter reporting periods by code, name or status.</p>
        </div>
        <div className="unit-filter-row">
          <InputGroup className="unit-search" style={{ width: 220 }}>
            <InputGroup.Text aria-hidden="true">
              <i className="fa-solid fa-magnifying-glass"></i>
            </InputGroup.Text>
            <Form.Control placeholder="Search code or name..." aria-label="Search code or name" value={search} onChange={(e) => setSearch(e.target.value)} />
          </InputGroup>
          <Form.Select style={{ maxWidth: 170 }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All Statuses</option>
            {Object.keys(STATUS_VARIANTS).map((s) => <option key={s} value={s}>{s}</option>)}
          </Form.Select>
          {hasActiveFilters && <Button className="unit-clear-filters-btn" onClick={clearFilters}>Clear Filters</Button>}
        </div>
        <div className="unit-table-wrapper">
          {loading ? <div className="unit-loader"><Spinner animation="border" /></div> : (
            <Table responsive className="unit-table">
              <thead><tr><th>Code</th><th>Name</th><th>Type</th><th>Fiscal Year</th><th>Dates</th><th>Status</th><th className="text-end">Actions</th></tr></thead>
              <tbody>
                {visiblePeriods.length ? visiblePeriods.map((p) => (
                  <tr key={p.id}>
                    <td>{p.code}</td>
                    <td>{p.name}</td>
                    <td>{p.period_type}</td>
                    <td>{p.fiscal_year_label || "—"}</td>
                    <td className="small">{String(p.start_date).slice(0, 10)} → {String(p.end_date).slice(0, 10)}</td>
                    <td><Badge className="perfperiod-status" bg={STATUS_VARIANTS[p.status] || "secondary"}>{p.status}</Badge></td>
                    <td>
                      <div className="unit-actions" style={{ flexWrap: "wrap" }}>
                        <Can moduleKey="performance_periods" actionKey="edit">
                          <Button className="unit-icon-btn unit-edit" title="Edit" aria-label={`Edit ${p.code || p.name || "period"}`} onClick={() => openEdit(p)}>
                            <i className="fa-solid fa-pen" aria-hidden="true"></i>
                          </Button>
                        </Can>
                        {p.status === "DRAFT" && (
                          <Button size="sm" variant="outline-success" title="Open" aria-label={`Open ${p.code || p.name || "period"}`} onClick={() => setStatus(p, "OPEN")}>
                            <i className="fa-solid fa-play" aria-hidden="true"></i> Open
                          </Button>
                        )}
                        {p.status === "OPEN" && (
                          <Button size="sm" variant="outline-secondary" title="Close" aria-label={`Close ${p.code || p.name || "period"}`} onClick={() => setStatus(p, "CLOSED")}>
                            <i className="fa-solid fa-lock" aria-hidden="true"></i> Close
                          </Button>
                        )}
                        {p.status === "CLOSED" && (
                          <Button size="sm" variant="outline-dark" title="Archive" aria-label={`Archive ${p.code || p.name || "period"}`} onClick={() => setStatus(p, "ARCHIVED")}>
                            <i className="fa-solid fa-box-archive" aria-hidden="true"></i> Archive
                          </Button>
                        )}
                        <Can moduleKey="performance_periods" actionKey="delete">
                          <Button className="unit-icon-btn unit-delete" title="Delete" aria-label={`Delete ${p.code || p.name || "period"}`} onClick={() => setDeleteTarget(p)}>
                            <i className="fa-solid fa-trash" aria-hidden="true"></i>
                          </Button>
                        </Can>
                      </div>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="7">
                      <EmptyState
                        icon={FaCalendarAlt}
                        title="No performance periods found"
                        message={hasActiveFilters ? "Try another search or clear filters." : "Create your first reporting period to get started."}
                        action={hasActiveFilters ? (
                          <Button className="unit-btn unit-btn-light" onClick={clearFilters}>Clear Filters</Button>
                        ) : (
                          <Can moduleKey="performance_periods" actionKey="create">
                            <Button className="unit-btn unit-btn-primary" onClick={openAdd}>
                              <i className="fa-solid fa-plus" aria-hidden="true"></i>
                              New Period
                            </Button>
                          </Can>
                        )}
                      />
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </div>
      </div>

      <Modal show={showModal} onHide={closeModal} centered className="unit-modal">
        <Modal.Header closeButton><Modal.Title>{editId ? "Edit Period" : "New Period"}</Modal.Title></Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col md={6}><Form.Label>Code</Form.Label><Form.Control value={form.code} disabled={!!editId} onChange={(e) => setForm({ ...form, code: e.target.value })} /></Col>
            <Col md={6}><Form.Label>Name</Form.Label><Form.Control value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Col>
            <Col md={6}>
              <Form.Label>Period Type</Form.Label>
              <Form.Select value={form.period_type} onChange={(e) => setForm({ ...form, period_type: e.target.value })}>
                {["MONTHLY", "QUARTERLY", "HALF_YEARLY", "ANNUAL", "CUSTOM"].map((t) => <option key={t} value={t}>{t}</option>)}
              </Form.Select>
            </Col>
            <Col md={6}><Form.Label>Fiscal Year Label (optional)</Form.Label><Form.Control placeholder="e.g. FY2026-27" value={form.fiscal_year_label} onChange={(e) => setForm({ ...form, fiscal_year_label: e.target.value })} /></Col>
            <Col md={6}><Form.Label>Start Date</Form.Label><Form.Control type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></Col>
            <Col md={6}><Form.Label>End Date</Form.Label><Form.Control type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button className="unit-btn unit-btn-light" onClick={closeModal}>Cancel</Button>
          <Button className="unit-btn unit-btn-primary" onClick={handleSave} disabled={saving}>{saving ? "Saving..." : "Save"}</Button>
        </Modal.Footer>
      </Modal>

      <ConfirmModal
        show={!!deleteTarget}
        onHide={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        busy={deleting}
        title="Delete Period"
        message={`Are you sure you want to delete "${deleteTarget?.name}"? This action cannot be undone. If any scorecard already references it, deletion will be blocked.`}
        confirmLabel="Delete"
      />
    </div>
  );
}
